/**
 * فیلدِ نامِ کاربریِ فرمِ ثبت‌نام — یک‌بار نوشته، دو جا استفاده.
 *
 * دو فرمِ ثبت‌نام داریم که باید مو به مو مثلِ هم رفتار کنند
 * (`apps/mobile/app/auth.tsx` و `apps/web/.../CreateProfilePage.tsx`).
 *
 * ---------- چرا فیلد خودش پر نمی‌شود ----------
 * اولین نسخه‌ی این هوک فیلد را از روی اسم پر می‌کرد و کاربر فقط تاییدش
 * می‌کرد. کنار گذاشته شد: نامِ کاربری تا ابد روی حساب می‌ماند و فیلدی که
 * از قبل پر است، خوانده نمی‌شود — آدم ردش می‌کند و بعد با اسمی می‌ماند که
 * هرگز انتخابش نکرده. حالا خالی شروع می‌شود و انتخاب صریح است.
 *
 * ولی خالی رها هم نمی‌شود: اگر کاربر بی‌آن‌که پرش کند «ساختن» را بزند، فرم
 * می‌گوید اجباری است و **همان لحظه** یک پیشنهاد جلویش می‌گذارد که با یک
 * لمس بنشیند (`suggest` و بعد `accept`). پیشنهاد از روی اسم ساخته می‌شود و
 * اگر اسم لاتین نبود از روی ایمیل — و پیش از عرضه، آزاد بودنش سنجیده
 * می‌شود، چون پیشنهادی که همان لحظه «گرفته شده» بگیرد بدتر از نبودنش است.
 *
 * ---------- بقیه‌ی رفتار ----------
 * آزاد بودنِ چیزی که کاربر تایپ می‌کند با کمی مکث پرسیده می‌شود (وگرنه سرِ
 * هر حرف یک کوئری) و نتیجه یک خط زیرِ خودِ فیلد است، نه خطایی بعد از زدنِ
 * دکمه.
 *
 * `state` مستقیم کلیدِ ترجمه نیست چون دو حالتش («خالی» و «نامعلوم») اصلاً
 * متنی ندارند؛ نگاشتش دستِ خودِ فرم است.
 */
import { useEffect, useState } from 'react';
import { profileRepo } from '../data/socialRepo.ts';
import { reportError } from './report.ts';
import {
  USERNAME_MIN, firstFreeUsername, normalizeUsername, suggestUsername,
} from './username.ts';

export type UsernameState =
  /** هنوز چیزی نوشته نشده. */
  | 'empty'
  /** کوتاه‌تر از `USERNAME_MIN`. */
  | 'short'
  /** منتظرِ جوابِ دیتابیس. */
  | 'checking'
  /** آزاد است. */
  | 'free'
  /** گرفته شده. */
  | 'taken'
  /**
   * نشد بپرسیم (شبکه، یا خطای دیتابیس).
   *
   * این حالت **جلوی ساختِ حساب را نمی‌گیرد**: یک قطعیِ لحظه‌ایِ شبکه نباید
   * کسی را پشتِ فرمِ ثبت‌نام حبس کند، و اگر واقعاً گرفته باشد قیدِ یکتاییِ
   * ستون همان لحظه‌ی درج جلویش را می‌گیرد و فرم پیامِ «گرفته شده» را
   * نشان می‌دهد. این‌جا فقط چیزی ادعا نمی‌کنیم.
   */
  | 'unknown';

/** چقدر بعد از آخرین حرف بپرسیم. کوتاه‌تر یعنی کوئریِ بی‌خود سرِ هر حرف. */
const DEBOUNCE_MS = 350;

export type UsernameField = {
  /** مقدارِ فیلد — همیشه پاک‌سازی‌شده، پس هرچه این‌جاست همان چیزی است که ذخیره می‌شود. */
  value: string;
  /** به `onChangeText` / `onChange` وصلش کن. */
  set: (raw: string) => void;
  state: UsernameState;
  /** یعنی چیزی در فیلد هست و می‌شود باهاش حساب ساخت. */
  ready: boolean;
  /**
   * پیشنهادِ آماده، یا `null`.
   *
   * تا وقتی `suggest()` صدا زده نشود همیشه `null` است — یعنی تا وقتی کاربر
   * با فیلدِ خالی «ساختن» را نزده، هیچ پیشنهادی جایی دیده نمی‌شود.
   */
  suggestion: string | null;
  /** «فیلد خالی بود و دکمه زده شد» — برو یک پیشنهادِ آزاد پیدا کن. */
  suggest: () => Promise<void>;
  /** کاربر پیشنهاد را قبول کرد؛ بنشان توی فیلد. */
  accept: () => void;
};

export function useUsernameField(
  displayName: string,
  email?: string | null,
): UsernameField {
  const [value, setValue] = useState('');
  const [state, setState] = useState<UsernameState>('empty');
  const [suggestion, setSuggestion] = useState<string | null>(null);

  const set = (raw: string) => {
    setValue(normalizeUsername(raw));
    // چیزی که خودش نوشت، جای هر پیشنهادی را می‌گیرد.
    setSuggestion(null);
  };

  const isFree = (u: string) => profileRepo.isUsernameFree(u);

  const suggest = async () => {
    const base = suggestUsername(displayName, email);
    // نه اسمِ لاتینی هست نه ایمیلی که چیزی ازش دربیاید — چیزی برای پیشنهاد
    // نداریم، و اسمِ ساختگی («user48213») همان چیزی است که از این فرم
    // بیرون انداختیم.
    if (!base) { setSuggestion(null); return; }
    try {
      // عدد ته می‌چسبد اگر لازم باشد؛ پیشنهادی که همان لحظه «گرفته شده»
      // بگیرد از نبودنِ پیشنهاد بدتر است.
      setSuggestion(await firstFreeUsername(base, isFree));
    } catch (e) {
      reportError('username.suggest', e);
      // نشد بپرسیم؛ خودِ پایه را عرضه می‌کنیم و اگر گرفته بود، همان خطِ
      // زیرِ فیلد بعد از نشستنش می‌گویدش.
      setSuggestion(base);
    }
  };

  const accept = () => {
    if (!suggestion) return;
    setValue(suggestion);
    setSuggestion(null);
  };

  // آزاد است؟
  useEffect(() => {
    if (value.length === 0) { setState('empty'); return; }
    if (value.length < USERNAME_MIN) { setState('short'); return; }

    setState('checking');
    let alive = true;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const free = await isFree(value);
          if (alive) setState(free ? 'free' : 'taken');
        } catch (e) {
          reportError('username.check', e);
          if (alive) setState('unknown');
        }
      })();
    }, DEBOUNCE_MS);

    return () => { alive = false; clearTimeout(timer); };
  }, [value]);

  return {
    value,
    set,
    state,
    ready: state === 'free' || state === 'unknown',
    suggestion,
    suggest,
    accept,
  };
}
